import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Search, UserPlus, X, Phone, Calendar } from "lucide-react";
import { toast } from "sonner";
import { segmentColor } from "../../lib/format";
import { validatePhone, sanitizePhone } from "../../lib/phoneValidation";

export function CustomerSearchPanel({ onSelect, selectedId }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 5) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/customers", { params: { q: query } });
        setResults(data);
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]"
          />
          <input
            data-testid="customer-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="Enter mobile number…"
            inputMode="numeric"
            autoComplete="off"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-10 pr-4 py-3 min-h-[48px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          />
        </div>
        <button
          data-testid="add-customer-button"
          onClick={() => setShowAdd(true)}
          className="bg-[#9D00FF] hover:bg-[#8C00E6] text-white rounded-lg px-4 min-h-[48px] font-semibold flex items-center gap-2 transition tb-glow"
        >
          <UserPlus size={18} />
          <span className="hidden sm:inline">Add</span>
        </button>
      </div>

      {query && results.length > 0 && (
        <div className="absolute z-30 mt-2 left-0 right-0 bg-[#1A1A1A] border border-white/10 rounded-lg overflow-hidden shadow-2xl max-h-72 overflow-y-auto">
          {results.map((c) => (
            <button
              key={c.id}
              data-testid={`customer-result-${c.id}`}
              onClick={() => {
                onSelect(c.id);
                setQuery("");
                setResults([]);
              }}
              className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#9D00FF]/10 border-b border-white/5 last:border-b-0 ${
                selectedId === c.id ? "bg-[#9D00FF]/10" : ""
              }`}
            >
              <div>
                <div className="font-medium text-white flex items-center gap-1.5">
                  <Phone size={12} />
                  <span><span className="text-[#9D00FF]">{c.phone.slice(0, Math.min(query.length, c.phone.length))}</span>{c.phone.slice(Math.min(query.length, c.phone.length))}</span>
                </div>
                <div className="text-xs text-[#A0A0A0] flex items-center gap-3 mt-0.5">
                  <span>{c.name}</span>
                  <span>{c.total_visits} visits</span>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-md border ${segmentColor(c.segment)}`}>
                {c.segment}
              </span>
            </button>
          ))}
        </div>
      )}

      {query.length >= 5 && !searching && results.length === 0 && (
        <div className="absolute z-30 mt-2 left-0 right-0 bg-[#1A1A1A] border border-white/10 rounded-lg p-4 text-sm text-[#A0A0A0]">
          No customer with this number. <button onClick={() => setShowAdd(true)} className="text-[#9D00FF] font-semibold">Add as new?</button>
        </div>
      )}

      {showAdd && (
        <AddCustomerDialog
          prefill={query}
          onClose={() => setShowAdd(false)}
          onCreated={(c) => {
            setShowAdd(false);
            setQuery("");
            setResults([]);
            onSelect(c.id);
          }}
        />
      )}
    </div>
  );
}

function AddCustomerDialog({ prefill = "", onClose, onCreated }) {
  const [name, setName] = useState(/^\d/.test(prefill) ? "" : prefill);
  const [phone, setPhone] = useState(/^\d/.test(prefill) ? prefill : "");
  const [phoneErr, setPhoneErr] = useState("");
  const [dob, setDob] = useState("");
  const [waOptIn, setWaOptIn] = useState(true);
  const [loading, setLoading] = useState(false);

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    setPhoneErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validatePhone(phone);
    if (err) { setPhoneErr(err); return; }
    const clean = sanitizePhone(phone);
    setLoading(true);
    try {
      const { data } = await api.post("/customers", {
        name: name.trim(), phone: clean, dob: dob || null,
        whatsapp_opt_in: waOptIn,
      });
      toast.success(`${data.name} added to TabOn`);
      onCreated(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-md p-6 tb-fade-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Add to TabOn</h2>
          <button onClick={onClose} data-testid="add-customer-close" className="text-[#A0A0A0] hover:text-white p-2">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Name *</label>
            <input
              data-testid="new-customer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Phone *</label>
            <input
              data-testid="new-customer-phone"
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              required
              maxLength={13}
              className={`w-full bg-[#0A0A0A] border rounded-lg px-4 py-3 min-h-[44px] text-white focus:ring-1 focus:ring-[#9D00FF]/40 ${phoneErr ? "border-red-500 focus:border-red-500" : "border-white/10 focus:border-[#9D00FF]"}`}
              placeholder="10-digit mobile number"
            />
            {phoneErr && <p className="text-xs text-red-400 mt-1">{phoneErr}</p>}
            {!phoneErr && <p className="text-[11px] text-[#6B6B6B] mt-1">📲 Points &amp; rewards will be sent via WhatsApp</p>}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
              <Calendar size={10} className="inline mr-1" /> Date of Birth (optional)
            </label>
            <input
              data-testid="new-customer-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
            />
          </div>
          {/* WhatsApp opt-in */}
          <div className="flex items-center justify-between bg-[#0F0F0F] border border-white/5 rounded-xl px-4 py-3">
            <div>
              <div className="text-xs font-bold text-white">WhatsApp Updates</div>
              <p className="text-[10px] text-[#A0A0A0] mt-0.5">Customer consents to receive points &amp; rewards via WhatsApp</p>
            </div>
            <button
              type="button"
              data-testid="wa-opt-in-toggle"
              onClick={() => setWaOptIn(!waOptIn)}
              className={`ml-4 shrink-0 min-w-[44px] min-h-[24px] rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${
                waOptIn ? "bg-[#9D00FF] text-white" : "bg-white/10 text-[#A0A0A0]"
              }`}
            >
              {waOptIn ? "On" : "Off"}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="new-customer-submit"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all"
          >
            {loading ? "Adding…" : "Add Customer"}
          </button>
        </form>
      </div>
    </div>
  );
}
